import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Store, LogOut, Trash2, ExternalLink, Copy, KeyRound, FileText, Gauge, Check, KeySquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { PRODUCT_TYPES } from "@/lib/productTypes";

export default function SuperAdmin() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "", owner_name: "", owner_email: "", owner_password: "",
        location: "", phone: "", whatsapp: "", default_product_types: ["saree"],
    });

    const load = async () => {
        try {
            const { data } = await api.get("/admin/shops");
            setShops(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const onCreate = async (e) => {
        e.preventDefault();
        if (!form.default_product_types?.length) {
            toast.error("Pick at least one product type");
            return;
        }
        setCreating(true);
        try {
            await api.post("/admin/shops", form);
            toast.success("Shop created");
            setOpen(false);
            setForm({ name: "", owner_name: "", owner_email: "", owner_password: "", location: "", phone: "", whatsapp: "", default_product_types: ["saree"] });
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create");
        } finally { setCreating(false); }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this shop and all its sarees?")) return;
        try {
            await api.delete(`/admin/shops/${id}`);
            toast.success("Shop deleted");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
        }
    };

    const onResetPassword = async (shop) => {        const custom = window.prompt(
            `Reset password for ${shop.owner_email}?\n\nLeave blank to auto-generate a secure password, or type a new one:`,
            ""
        );
        if (custom === null) return;
        try {
            const body = custom.trim() ? { new_password: custom.trim() } : {};
            const { data } = await api.post(`/admin/shops/${shop.id}/reset-owner-password`, body);
            const msg = `Password reset for ${data.owner_email}\n\nNew password: ${data.new_password}\n\nThis is shown ONCE — copy it now.`;
            window.alert(msg);
            try { await navigator.clipboard.writeText(`${data.owner_email} / ${data.new_password}`); } catch {}
            toast.success("Password reset + copied to clipboard");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
        }
    };

    const onEditQuota = async (shop) => {
        const cur = shop.daily_quota ?? 100;
        const v = window.prompt(`Daily AI try-on quota for ${shop.name}\n\nCurrent: ${cur}/day. Enter a new limit (1–100000):`, String(cur));
        if (v === null) return;
        const n = parseInt(v, 10);
        if (!Number.isFinite(n) || n < 1) { toast.error("Quota must be a positive integer"); return; }
        try {
            await api.patch(`/admin/shops/${shop.id}`, { daily_quota: n });
            toast.success(`Quota set to ${n}/day`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
        }
    };

    const doLogout = async () => { await logout(); nav("/login"); };

    const downloadOnboardingKit = async (shop) => {
        try {
            const res = await api.get(`/admin/shops/${shop.id}/onboarding-kit`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `vastra-onboarding-${shop.slug}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Kit downloaded for ${shop.name}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
        }
    };

    return (
        <div className="min-h-screen bg-brand-beige">
            <header className="ayraa-glass sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="font-serif text-2xl tracking-tight">Vastra<span className="text-brand-primary">.</span>AI</Link>
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:block text-sm text-brand-muted">Super Admin · {user?.email}</span>
                        <button className="ayraa-btn-ghost text-sm" onClick={doLogout} data-testid="logout-btn">
                            <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="ayraa-overline">Super admin</div>
                        <h1 className="font-serif text-4xl sm:text-5xl mt-2">Boutiques across Vastra</h1>
                        <p className="text-brand-muted mt-2">Grant access, monitor catalogs, manage every storefront.</p>
                    </div>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="ayraa-btn-primary" data-testid="new-shop-btn">
                                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New shop
                            </button>
                        </DialogTrigger>
                        <DialogContent className="bg-brand-paper border-brand-border max-w-lg">
                            <DialogHeader><DialogTitle className="font-serif text-2xl">Create a shop</DialogTitle></DialogHeader>
                            <form onSubmit={onCreate} className="space-y-3" data-testid="new-shop-form">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <label className="ayraa-overline">Shop name</label>
                                        <input className="ayraa-input mt-1.5" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="shop-name-input" />
                                    </div>
                                    <div>
                                        <label className="ayraa-overline">Owner name</label>
                                        <input className="ayraa-input mt-1.5" required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="ayraa-overline">Owner email</label>
                                        <input type="email" className="ayraa-input mt-1.5" required value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="ayraa-overline">Owner password</label>
                                        <input type="text" className="ayraa-input mt-1.5" required minLength={6} value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="ayraa-overline">Phone</label>
                                        <input className="ayraa-input mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="ayraa-overline">Location</label>
                                        <input className="ayraa-input mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="T. Nagar, Chennai" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="ayraa-overline">WhatsApp number</label>
                                        <input className="ayraa-input mt-1.5" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+91…" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="ayraa-overline">Product types this shop sells</label>
                                        <div className="mt-2 grid grid-cols-2 gap-2 p-3 rounded-lg border border-brand-border bg-white/40" data-testid="shop-product-types-checkboxes">
                                            {PRODUCT_TYPES.map((p) => {
                                                const checked = form.default_product_types?.includes(p.value);
                                                return (
                                                    <label key={p.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white/60 px-2 py-1.5 rounded">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!checked}
                                                            onChange={(e) => {
                                                                const cur = form.default_product_types || [];
                                                                const next = e.target.checked
                                                                    ? [...cur, p.value]
                                                                    : cur.filter((v) => v !== p.value);
                                                                setForm({ ...form, default_product_types: next });
                                                            }}
                                                            data-testid={`shop-pt-${p.value}`}
                                                        />
                                                        <span>{p.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <p className="text-xs text-brand-soft mt-1.5">
                                            Tick every category this boutique stocks. Owner can pick from these when uploading items.
                                        </p>
                                    </div>
                                </div>
                                <DialogFooter className="pt-3">
                                    <button type="submit" className="ayraa-btn-primary w-full" disabled={creating} data-testid="shop-create-submit">
                                        {creating ? "Creating…" : "Create shop & owner"}
                                    </button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                <div className="ayraa-divider my-10" />

                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => <div key={i} className="ayraa-card h-44 skel" />)}
                    </div>
                ) : shops.length === 0 ? (
                    <div className="ayraa-card p-10 text-center">
                        <Store className="w-10 h-10 mx-auto text-brand-gold" strokeWidth={1.2} />
                        <div className="font-serif text-2xl mt-4">No shops yet</div>
                        <p className="text-brand-muted mt-2">Create your first storefront to begin.</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shops-grid">
                        {shops.map((s) => (
                            <div key={s.id} className="ayraa-card p-6 flex flex-col" data-testid={`shop-card-${s.slug}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="ayraa-overline">/{s.slug}</div>
                                        <div className="font-serif text-2xl mt-1">{s.name}</div>
                                        <div className="text-sm text-brand-muted mt-1">{s.location || "—"}</div>
                                    </div>
                                    <button className="ayraa-btn-ghost text-brand-primary p-2" onClick={() => onDelete(s.id)} title="Delete shop" data-testid={`shop-delete-${s.slug}`}>
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                                    <div><div className="ayraa-overline">Owner</div><div className="mt-1 truncate">{s.owner_name}</div></div>
                                    <div><div className="ayraa-overline">Sarees</div><div className="mt-1">{s.saree_count}</div></div>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-2 text-sm" data-testid={`shop-ai-stats-${s.slug}`}>
                                    <div>
                                        <div className="ayraa-overline flex items-center gap-1"><Gauge className="w-3 h-3" strokeWidth={1.5} /> AI today</div>
                                        <div className="mt-1" data-testid={`shop-quota-${s.slug}`}>
                                            <span className="font-medium">{s.today_platform_count ?? 0}</span>
                                            <span className="text-brand-soft"> / {s.daily_quota ?? 100}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="ayraa-overline flex items-center gap-1"><KeySquare className="w-3 h-3" strokeWidth={1.5} /> BYOK</div>
                                        <div className="mt-1 text-sm">
                                            {s.has_byok_key ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3.5 h-3.5" strokeWidth={2} /> Active</span>
                                            ) : (
                                                <span className="text-brand-soft">Platform key</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 flex items-center gap-2">
                                    <Link to={`/shop/${s.slug}`} className="ayraa-btn-outline flex-1 py-2 text-sm" data-testid={`shop-view-${s.slug}`}>
                                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Open
                                    </Link>
                                    <button
                                        className="ayraa-btn-ghost text-sm"
                                        onClick={() => onResetPassword(s)}
                                        title="Reset owner password"
                                        data-testid={`shop-reset-pw-${s.slug}`}
                                    >
                                        <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    </button>
                                    <button
                                        className="ayraa-btn-ghost text-sm"
                                        onClick={() => downloadOnboardingKit(s)}
                                        title="Download onboarding kit PDF"
                                        data-testid={`shop-kit-${s.slug}`}
                                    >
                                        <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    </button>
                                    <button
                                        className="ayraa-btn-ghost text-sm"
                                        onClick={() => onEditQuota(s)}
                                        title="Edit daily AI quota"
                                        data-testid={`shop-quota-edit-${s.slug}`}
                                    >
                                        <Gauge className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    </button>
                                    <button
                                        className="ayraa-btn-ghost text-sm"
                                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/shop/${s.slug}`); toast.success("Link copied"); }}
                                        data-testid={`shop-copy-${s.slug}`}
                                    >
                                        <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
